import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Select, { SingleValue } from 'react-select'
import { CircularProgress } from '@mui/material'

import ConfirmationModal from '../../components/containers/ConfirmationModal'
import { useAppDispatch } from '../../redux/store'
import {
  selectSpecificationLoading,
  selectSpecifications,
} from '../../redux/specification/specificationSelectors'
import { createSpecificationAction } from '../../redux/specification/specificationAction'
import { CURRENCY_CODES, getCurrencyLabel } from '../../constants/currencies'
import NormList from '../../components/containers/NormList.tsx'
import { useNormCategories } from '../../hooks/useNormCategories'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { formatNormLabel } from '../../utils/normLabels'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import i18n from '../../i18n'
import './selectNorm.css'

const NORM_META: Record<string, { short: string; hintKey: string }> = {
  NIST: { short: 'NIST', hintKey: 'nist' },
  ISO_13: { short: 'ISO 2013', hintKey: 'iso13' },
  ISO_22: { short: 'ISO 2022', hintKey: 'iso22' },
  NIS2: { short: 'NIS2', hintKey: 'nis2' },
  SOC: { short: 'SOC', hintKey: 'soc' },
  SWIFT: { short: 'SWIFT', hintKey: 'swift' },
  SAMA: { short: 'SAMA', hintKey: 'sama' },
}

type Option = { value: string; label: string }

function NormTiles({
  categories,
  selected,
  multi,
  onSelect,
}: {
  categories: string[]
  selected: string | string[] | null
  multi?: boolean
  onSelect: (value: string | string[]) => void
}) {
  const { t } = useTranslation()

  const isActive = (cat: string) =>
    multi
      ? Array.isArray(selected) && selected.includes(cat)
      : selected === cat

  const toggle = (cat: string) => {
    if (multi) {
      const cur = Array.isArray(selected) ? selected : []
      onSelect(
        cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat]
      )
    } else {
      onSelect(cat)
    }
  }

  return (
    <div className="sn-norm-grid" role={multi ? 'group' : 'radiogroup'}>
      {categories.map((cat) => {
        const meta = NORM_META[cat] || {
          short: formatNormLabel(cat),
          hintKey: '',
        }
        const active = isActive(cat)
        return (
          <button
            key={cat}
            type="button"
            role={multi ? 'checkbox' : 'radio'}
            aria-checked={active}
            title={formatNormLabel(cat)}
            className={`sn-norm-card${active ? ' is-active' : ''}`}
            onClick={() => toggle(cat)}
          >
            <span className="sn-norm-card-top">
              <span className="sn-norm-card-mark">
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2.5 6.2L4.8 8.5 9.5 3.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="sn-norm-card-label">{meta.short}</span>
            </span>
            <span className="sn-norm-card-hint">
              {meta.hintKey
                ? t(`settingsPage.normHints.${meta.hintKey}`)
                : formatNormLabel(cat)}
            </span>
          </button>
        )
      })}
    </div>
  )
}

const SelectNorm = () => {
  const { t } = useTranslation()
  const menu = useExclusiveMenu()
  const [currency, setCurrency] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    'ISO_22'
  )
  const [
    selectedIndustrialApplicableFrameworks,
    setSelectedIndustrialApplicableFrameworks,
  ] = useState<string[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const username = useSelector(selectUsername)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const loading = useSelector(selectSpecificationLoading)
  const settings = useSelector(selectSpecifications)
  const { categories, industrialApplicableFrameworks } = useNormCategories()

  const containerRef = useRef<HTMLDivElement | null>(null)

  const currencyOptions = useMemo<Option[]>(
    () =>
      CURRENCY_CODES.map((code) => ({
        value: code,
        label: getCurrencyLabel(code, i18n.language),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  )
  const selectedCurrencyOption =
    currencyOptions.find((o) => o.value === currency) || null

  useEffect(() => {
    if (settings[0]?.NormBase) navigate('/', { replace: true })
  }, [settings, navigate])

  useEffect(() => {
    const html = document.documentElement
    html.classList.add('sn-canvas')
    return () => {
      html.classList.remove('sn-canvas')
    }
  }, [])


  const handleConfirm = useCallback(() => setShowConfirmModal(true), [])

  const confirmSelection = useCallback(async () => {
    try {
      await dispatch(
        createSpecificationAction({
          NormBase: selectedCategory as string,
          Currency: currency,
          Language: i18n.language.toLocaleUpperCase(),
          IndustrialApplicableFrameworks:
            selectedIndustrialApplicableFrameworks,
        })
      ).unwrap()
      setShowConfirmModal(false)
      navigate('/')
    } catch (error) {
      console.error('Failed to create specification:', error)
    }
  }, [
    currency,
    selectedCategory,
    selectedIndustrialApplicableFrameworks,
    dispatch,
    navigate,
  ])

  const isDone =
    !!currency &&
    !!selectedCategory &&
    selectedIndustrialApplicableFrameworks.length > 0
  const completedSteps = [
    !!currency,
    !!selectedCategory,
    selectedIndustrialApplicableFrameworks.length > 0,
  ].filter(Boolean).length

  return (
    <div className="page-content">
      <div className="sn-page" ref={containerRef}>
        <header className="sn-head">
          <div>
            <p className="sn-eyebrow">
              <span className="sn-eyebrow-dot">1</span>
              {t('selectNormPage.eyebrow', 'Workspace setup')}
            </p>
            <h1 className="sn-title">
              {t('selectNormPage.greeting', { username })}
            </h1>
            <div className="sn-title-bar" aria-hidden />
            <p className="sn-sub">{t('selectNormPage.instruction')}</p>
          </div>

          <div className="sn-head-aside">
            <div className="sn-progress" aria-label="Progress">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`sn-progress-seg${
                    i < completedSteps ? ' is-on' : ''
                  }`}
                />
              ))}
              <span className="sn-progress-label">{completedSteps}/3</span>
            </div>
            <button
              type="button"
              className="sn-save"
              disabled={!isDone || loading}
              onClick={handleConfirm}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <>
                  {t('selectNormPage.saveButton')}
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden
                  >
                    <path
                      d="M2.5 8h11M9.5 4.5L13 8l-3.5 3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </header>

        <div className="sn-layout">
          <div className="sn-col">
            <section className="sn-card">
              <div className="sn-card-head">
                <span className={`sn-card-n${currency ? ' is-done' : ''}`}>
                  {currency ? '✓' : '1'}
                </span>
                <div className="sn-card-copy">
                  <h3>{t('selectNormPage.currencyTitle', 'Currency')}</h3>
                  <p>
                    {t(
                      'selectNormPage.currencyDesc',
                      'Used for budgets, costs, and financial indicators.'
                    )}
                  </p>
                </div>
              </div>
              <div className="sn-card-body">
                <Select
                  classNamePrefix="incyia-sn"
                  value={selectedCurrencyOption}
                  options={currencyOptions}
                  isSearchable
                  placeholder={t('selectNormPage.currencyLabel')}
                  noOptionsMessage={() => t('tables.noData')}
                  {...menu}
                  styles={incyiaSelectStyles}
                  onChange={(selected: SingleValue<Option>) =>
                    setCurrency(selected?.value || '')
                  }
                />
              </div>
            </section>

            <section className="sn-card">
              <div className="sn-card-head">
                <span
                  className={`sn-card-n${selectedCategory ? ' is-done' : ''}`}
                >
                  {selectedCategory ? '✓' : '2'}
                </span>
                <div className="sn-card-copy">
                  <h3>
                    {t('selectNormPage.normTitle', 'Base security norm')}
                  </h3>
                  <p>
                    {t(
                      'selectNormPage.normDesc',
                      'Your primary framework for controls, maturity, and reporting.'
                    )}
                  </p>
                </div>
              </div>
              <div className="sn-card-body">
                <NormTiles
                  categories={categories}
                  selected={selectedCategory}
                  onSelect={(v) => setSelectedCategory(v as string)}
                />
                {selectedCategory ? (
                  <NormList selectedCategory={selectedCategory} />
                ) : null}
              </div>
            </section>
          </div>

          <div className="sn-col">
            <section className="sn-card">
              <div className="sn-card-head">
                <span
                  className={`sn-card-n${
                    selectedIndustrialApplicableFrameworks.length
                      ? ' is-done'
                      : ''
                  }`}
                >
                  {selectedIndustrialApplicableFrameworks.length ? '✓' : '3'}
                </span>
                <div className="sn-card-copy">
                  <h3>
                    {t(
                      'selectNormPage.frameworksTitle',
                      'Industrial frameworks'
                    )}
                  </h3>
                  <p>
                    {t(
                      'selectNormPage.frameworksDesc',
                      'Standards you also map against for coverage and alignment.'
                    )}
                  </p>
                </div>
              </div>
              <div className="sn-card-body">
                <div className="sn-tools">
                  <span className="sn-tools-count">
                    {t('selectNormPage.selectedCount', {
                      count: selectedIndustrialApplicableFrameworks.length,
                      total: industrialApplicableFrameworks.length,
                    })}
                  </span>
                  <div className="sn-tools-actions">
                    <button
                      type="button"
                      className="sn-tool-btn"
                      disabled={
                        selectedIndustrialApplicableFrameworks.length ===
                        industrialApplicableFrameworks.length
                      }
                      onClick={() =>
                        setSelectedIndustrialApplicableFrameworks([
                          ...industrialApplicableFrameworks,
                        ])
                      }
                    >
                      {t('selectNormPage.selectAll')}
                    </button>
                    <button
                      type="button"
                      className="sn-tool-btn"
                      disabled={!selectedIndustrialApplicableFrameworks.length}
                      onClick={() =>
                        setSelectedIndustrialApplicableFrameworks([])
                      }
                    >
                      {t('selectNormPage.clearAll')}
                    </button>
                  </div>
                </div>

                <NormTiles
                  multi
                  categories={industrialApplicableFrameworks}
                  selected={selectedIndustrialApplicableFrameworks}
                  onSelect={(v) =>
                    setSelectedIndustrialApplicableFrameworks(v as string[])
                  }
                />

                <div className="sn-aside-note">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    aria-hidden
                  >
                    <circle cx="8" cy="8" r="5.5" />
                    <path d="M8 7.2v3.5M8 5.2h.01" strokeLinecap="round" />
                  </svg>
                  <span>
                    {t(
                      'selectNormPage.frameworksNote',
                      'You can change these later in Settings. Pick every framework your organisation applies today.'
                    )}
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmSelection}
        loading={loading}
        selectedCategory={selectedCategory}
        currency={currency}
        frameworks={selectedIndustrialApplicableFrameworks}
      />
    </div>
  )
}

export default SelectNorm
